import React, { useRef, useEffect, useState } from 'react'
import * as d3 from 'd3'
import useMeasure from 'react-use-measure'
import Glow from '../../../../glows'

const UpsetChart = ({ data, setNames, setIsOpen }) => {
  const svgRef = useRef()
  const [ref, bounds] = useMeasure()
  const yAxisTitle = 'Intersection Size'
  const [selectedIntersection, setSelectedIntersection] = useState(
    Math.floor(Math.random() * data.length)
  )
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    const isSmallScreen = screenWidth <= 1025

    const margin = {
      top: isSmallScreen ? 50 : 20,
      right: 60,
      bottom: isSmallScreen ? 175 : 250,
      left: isSmallScreen ? 125 : 200,
    }
    if (
      !data ||
      data.length === 0 ||
      !setNames ||
      setNames.length === 0 ||
      !bounds.width ||
      !bounds.height
    ) {
      const svg = d3.select(svgRef.current)
      svg.selectAll('*').remove()
      return
    }

    // --- Configuration ---
    const minRectSize = 4
    const maxRectSize = 18
    const rectPadding = 10
    const matrixOffset = 50
    const connectorWidth = 2
    const labelOffset = 10
    const bandPaddingInner = 0.3
    const bandPaddingOuter = 0.2
    const minFontSize = 8
    const maxFontSize = 12

    // --- Colors ---
    const barColor = '#3EB6FB'
    const matrixColorPresent = '#3EB6FB'
    const matrixColorAbsent = '#363636'
    const connectorColor = '#3EB6FB'
    const highlightColor = '#FFFFFF'
    const selectedColor = '#FFFFFF'

    // --- Dimensions ---
    const availableWidth = bounds.width - margin.left - margin.right
    const height = bounds.height - margin.top - margin.bottom

    if (availableWidth <= 0 || height <= 0) {
      const svg = d3.select(svgRef.current)
      svg.selectAll('*').remove()
      return
    }

    // --- SVG ---
    const svg = d3.select(svgRef.current)
    svg.selectAll('*').remove()
    svg.attr('width', bounds.width).attr('height', bounds.height)
    const g = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`)

    // --- Scales ---
    const xScale = d3
      .scaleBand()
      .domain(d3.range(data.length))
      .range([0, availableWidth])
      .paddingInner(bandPaddingInner)
      .paddingOuter(bandPaddingOuter)

    const yScale = d3
      .scaleLinear()
      .range([height, 0])
      .domain([0, d3.max(data.map((d) => d.size)) || 1])

    const bandWidth = xScale.bandwidth()

    // --- Dynamic Size Calculations ---
    const dynamicFontSize = Math.max(
      minFontSize,
      Math.min(maxFontSize, Math.floor(availableWidth / 60))
    )
    const calculatedRectSize = bandWidth * 1
    const dynamicRectSize = Math.max(
      minRectSize,
      Math.min(maxRectSize, calculatedRectSize)
    )

    // --- Data with Indices ---
    const indexedData = data.map((d, i) => ({
      ...d,
      intersectionIndex: i,
    }))

    const matrixData = []
    const connectorData = []

    indexedData.forEach((intersection, i) => {
      const bandStart = xScale(i)
      if (bandStart === undefined) return
      const bandCenter = bandStart + bandWidth / 2

      // Matrix Rectangles Data
      intersection.sets.forEach((isPresent, j) => {
        matrixData.push({
          intersectionIndex: i,
          setIndex: j,
          isPresent: isPresent,
          x: bandCenter - dynamicRectSize / 2,
          y: height + matrixOffset + j * (dynamicRectSize + rectPadding),
          width: dynamicRectSize,
          height: dynamicRectSize,
        })
      })

      // Connector Data
      const activeIndices = intersection.sets
        .map((value, index) => (value === 1 ? index : -1))
        .filter((index) => index !== -1)

      if (activeIndices.length >= 2) {
        const firstIndex = Math.min(...activeIndices)
        const lastIndex = Math.max(...activeIndices)
        const yStart =
          height +
          matrixOffset +
          firstIndex * (dynamicRectSize + rectPadding) +
          dynamicRectSize / 2
        const yEnd =
          height +
          matrixOffset +
          lastIndex * (dynamicRectSize + rectPadding) +
          dynamicRectSize / 2

        connectorData.push({
          intersectionIndex: i,
          x: bandCenter - connectorWidth / 2,
          y: yStart,
          width: connectorWidth,
          height: Math.max(0, yEnd - yStart),
        })
      }
    })

    // --- Shared Hover and Click Handlers ---
    const handleMouseOver = (_event, d) => {
      const idx = d.intersectionIndex
      if (idx === undefined) return

      // Highlight Bar
      g.selectAll('.bar')
        .filter((b) => b.intersectionIndex === idx)
        .attr(
          'fill',
          idx === selectedIntersection ? selectedColor : highlightColor
        )

      // Highlight blue matrix rects in this column
      g.selectAll('.matrix-rect')
        .filter((m) => m.intersectionIndex === idx && m.isPresent === 1)
        .attr(
          'fill',
          idx === selectedIntersection ? selectedColor : highlightColor
        )

      // Highlight connector in this column
      g.selectAll('.connector')
        .filter((c) => c.intersectionIndex === idx)
        .attr(
          'fill',
          idx === selectedIntersection ? selectedColor : highlightColor
        )
    }

    const handleMouseOut = (event, d) => {
      const idx = d.intersectionIndex
      if (idx === undefined) return

      // Skip if this is the selected item
      if (idx === selectedIntersection) return

      // Restore Bar
      g.selectAll('.bar')
        .filter((b) => b.intersectionIndex === idx)
        .attr('fill', barColor)

      // Restore blue matrix rects in this column
      g.selectAll('.matrix-rect')
        .filter((m) => m.intersectionIndex === idx && m.isPresent === 1)
        .attr('fill', matrixColorPresent)

      // Restore connector in this column
      g.selectAll('.connector')
        .filter((c) => c.intersectionIndex === idx)
        .attr('fill', connectorColor)
    }

    const handleClick = (event, d) => {
      const idx = d.intersectionIndex
      if (idx === undefined) return

      // Reset previous selection
      if (selectedIntersection !== null) {
        // Reset previous bar
        g.selectAll('.bar')
          .filter((b) => b.intersectionIndex === selectedIntersection)
          .attr('fill', barColor)

        // Reset previous matrix rects
        g.selectAll('.matrix-rect')
          .filter(
            (m) =>
              m.intersectionIndex === selectedIntersection && m.isPresent === 1
          )
          .attr('fill', matrixColorPresent)

        // Reset previous connector
        g.selectAll('.connector')
          .filter((c) => c.intersectionIndex === selectedIntersection)
          .attr('fill', connectorColor)
      }

      // Set new selection
      setSelectedIntersection(idx)

      // Highlight new selection
      g.selectAll('.bar')
        .filter((b) => b.intersectionIndex === idx)
        .attr('fill', selectedColor)

      g.selectAll('.matrix-rect')
        .filter((m) => m.intersectionIndex === idx && m.isPresent === 1)
        .attr('fill', selectedColor)

      g.selectAll('.connector')
        .filter((c) => c.intersectionIndex === idx)
        .attr('fill', selectedColor)
    }

    // Draw Matrix Rectangles
    g.selectAll('.matrix-rect')
      .data(matrixData)
      .enter()
      .append('rect')
      .attr('class', 'matrix-rect')
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('width', (d) => d.width)
      .attr('height', (d) => d.height)
      .attr('fill', (d) => {
        if (d.isPresent === 1) {
          return d.intersectionIndex === selectedIntersection
            ? selectedColor
            : matrixColorPresent
        }
        return matrixColorAbsent
      })
      .style('cursor', (d) => (d.isPresent === 1 ? 'pointer' : 'default'))
      .filter((d) => d.isPresent === 1)
      .on('mouseover', handleMouseOver)
      .on('mouseout', handleMouseOut)
      .on('click', handleClick)

    // Draw Connectors
    g.selectAll('.connector')
      .data(connectorData)
      .enter()
      .append('rect')
      .attr('class', 'connector')
      .attr('x', (d) => d.x)
      .attr('y', (d) => d.y)
      .attr('width', (d) => d.width)
      .attr('height', (d) => d.height)
      .attr('fill', (d) =>
        d.intersectionIndex === selectedIntersection
          ? selectedColor
          : connectorColor
      )
      .style('cursor', 'pointer')
      .on('mouseover', handleMouseOver)
      .on('mouseout', handleMouseOut)
      .on('click', handleClick)

    // Matrix Row Labels
    g.selectAll('.set-name')
      .data(setNames)
      .enter()
      .append('text')
      .attr('class', 'set-name')
      .attr('x', -labelOffset)
      .attr(
        'y',
        (d, i) =>
          height +
          matrixOffset +
          i * (dynamicRectSize + rectPadding) +
          dynamicRectSize / 2
      )
      .attr('dy', '0.32em')
      .attr('fill', 'white')
      .style('text-anchor', 'end')
      .style('font-size', `${dynamicFontSize}px`)
      .text((d) => d)

    // --- Y Axis ---
    const yAxisGenerator = d3
      .axisLeft(yScale)
      .ticks(5)
      .tickSizeOuter(0)
      .tickSize(0)
    const yAxisGroup = g
      .append('g')
      .attr('class', 'y-axis')
      .call(yAxisGenerator)
    yAxisGroup.select('.domain').attr('stroke', '#78849E')
    yAxisGroup.selectAll('text').attr('fill', '#78849E')

    // Draw Y-Axis Title
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', -margin.left + 45)
      .attr('x', -height / 2)
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', isSmallScreen ? '10px' : '18px')
      .attr('fill', '#78849E')
      .text(yAxisTitle)

    // --- Grid Lines ---
    const numTicks = 5
    const ticks = yScale.ticks(numTicks)
    g.selectAll('.grid-line')
      .data(ticks)
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', availableWidth)
      .attr('y1', (d) => yScale(d))
      .attr('y2', (d) => yScale(d))
      .attr('stroke', '#363636')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', (d) => (d === 0 ? null : '4 4'))

    // --- Bars ---
    g.selectAll('.bar')
      .data(indexedData)
      .enter()
      .append('rect')
      .attr('class', 'bar')
      .attr('x', (d, i) => xScale(i) ?? 0)
      .attr('y', (d) => yScale(d.size))
      .attr('width', bandWidth)
      .attr('height', (d) => Math.max(0, height - yScale(d.size)))
      .attr('fill', (d) =>
        d.intersectionIndex === selectedIntersection ? selectedColor : barColor
      )
      .style('cursor', 'pointer')
      .on('mouseover', handleMouseOver)
      .on('mouseout', handleMouseOut)
      .on('click', handleClick)

    // --- Bar Labels ---
    g.selectAll('.bar-label')
      .data(data)
      .enter()
      .append('text')
      .attr('class', 'bar-label')
      .attr('x', (_d, i) => {
        const position = (xScale(i) ?? 0) + bandWidth / 2
        return isSmallScreen ? position + dynamicFontSize / 3 : position
      })
      .attr('y', (d) => {
        const baseY = yScale(d.size) - 5
        return isSmallScreen ? baseY - 10 : baseY
      })
      .attr('text-anchor', isSmallScreen ? 'start' : 'middle')
      .style('font-size', isSmallScreen ? '7px' : '9px')
      .attr('fill', 'white')
      .attr('transform', (_d, i) => {
        if (isSmallScreen) {
          const x = (xScale(i) ?? 0) + bandWidth / 2 + dynamicFontSize / 3
          const y = yScale(_d.size) - 15
          return `rotate(-90, ${x}, ${y})`
        }
        return null
      })
      .text((d) => d.size)

    // Apply default selection to the first item on initial render
    if (selectedIntersection === 0 && data.length > 0) {
      // Highlight default selection
      g.selectAll('.bar')
        .filter((b) => b.intersectionIndex === 0)
        .attr('fill', selectedColor)

      g.selectAll('.matrix-rect')
        .filter((m) => m.intersectionIndex === 0 && m.isPresent === 1)
        .attr('fill', selectedColor)

      g.selectAll('.connector')
        .filter((c) => c.intersectionIndex === 0)
        .attr('fill', selectedColor)
    }
  }, [
    data,
    setNames,
    bounds.width,
    bounds.height,
    yAxisTitle,
    selectedIntersection,
    screenWidth,
  ])

  const activeSetNames =
    selectedIntersection !== null && data[selectedIntersection]
      ? data[selectedIntersection].sets
          .map((isActive, index) => (isActive === 1 ? setNames[index] : null))
          .filter((name) => name !== null)
      : []

  // Get the size value for the current intersection
  const intersectionSize =
    selectedIntersection !== null && data[selectedIntersection]
      ? data[selectedIntersection].size
      : 0

  const selectedSkills =
    (selectedIntersection !== null && data[selectedIntersection]?.skills) || []

  // Format the active set names for display
  const formatSetNames = (names) => {
    if (names.length === 0) return ''
    if (names.length === 1) return names[0]
    if (names.length === 2) return `${names[0]} and ${names[1]}`

    const lastItem = names[names.length - 1]
    const allButLast = names.slice(0, -1)
    return `${allButLast.join(', ')}, and ${lastItem}`
  }

  return (
    <div className='upset-wrapper'>
      <div
        ref={ref}
        className='upset-container'
        style={{
          width: '100%',
          height: '750px',
          overflowY: 'hidden',
          overflowX: 'hidden',
        }}
      >
        <svg ref={svgRef}></svg>
      </div>

      {selectedIntersection !== null && (
        <div className='report-section'>
          <p className='skills-description'>
            There are {intersectionSize} keywords that are unique to{' '}
            {formatSetNames(activeSetNames)}.
          </p>
          <p className='skills-description'>
            Below are the top recurring skills among those keywords.
          </p>

          {selectedSkills.length > 0 && (
            <div className='skills-pills'>
              {selectedSkills.map((skill, index) => (
                <span key={index} className='skill-pill'>
                  {skill}
                </span>
              ))}
            </div>
          )}
          <hr className='section-divider' />

          <p className='skills-description'>
            For methodology and comprehensive view, download a report.
          </p>

          <div className='nsf-button-container'>
            <button
              className='nsf-button'
              onClick={() => {
                setIsOpen(true)
              }}
            >
              Download Report
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default UpsetChart
